import { Form, Input, Select } from "../../../../../../shared/frontend/ui";
import React from "react";
import { useI18n } from "../../app/i18n/useI18n";
import { FormDrawer } from "../FormDrawer";
import { useFormDirty } from "../../app/utils/useFormDirty";
import type { DocumentAppearanceDto } from "../../pages/settings/documentAppearance/documentAppearanceApi";

/**
 * Die Angaben, die **eine einzelne Sendung** abweichend stellen darf.
 *
 * Bewusst nicht dieselben wie im Textsatz: Studio, Kanal, Kategorie und Bezeichnung beschreiben die
 * Vorlage und gelten für jede künftige Sendung — hier geht es nur um dieses eine Schreiben.
 */
export type SendDocumentSettingsValues = {
  /** Der Titel in der digitalen Akte; leer heißt „Betreff verwenden". */
  documentTitle?: string | null;
  subject?: string | null;
  documentAppearanceId?: string | null;
};

const FORM_ID = "send-document-settings-form";

type Props = {
  open: boolean;
  appearances: DocumentAppearanceDto[];
  /** Der Stand, mit dem der Drawer aufgeht; er wird bei jedem Öffnen neu gestellt. */
  initialValues?: Partial<SendDocumentSettingsValues>;
  /**
   * Ob das Erscheinungsbild für diese Sendung gewählt werden darf. Ohne das Recht bleibt das Feld
   * sichtbar und gesperrt — sonst wäre unklar, warum das Briefpapier nicht zu ändern ist.
   */
  canChooseAppearance: boolean;
  onCancel: () => void;
  onFinish: (values: SendDocumentSettingsValues) => void;
};

export function SendDocumentSettingsDrawer({
  open,
  appearances,
  initialValues,
  canChooseAppearance,
  onCancel,
  onFinish,
}: Props) {
  const { text } = useI18n();
  const [form] = Form.useForm<SendDocumentSettingsValues>();
  const { dirty, initialize } = useFormDirty(form);

  /**
   * Bei jedem Öffnen neu stellen, nicht nur beim ersten: Ant Design hält die Werte eines Formulars
   * auch dann, wenn seine Felder zwischenzeitlich nicht im Dokument stehen — ein Abbruch trüge die
   * verworfenen Eingaben sonst wieder herein.
   */
  const initialValuesRef = React.useRef(initialValues);
  React.useEffect(() => {
    initialValuesRef.current = initialValues;
  }, [initialValues]);
  React.useEffect(() => {
    if (!open) return;
    form.resetFields();
    const values = initialValuesRef.current;
    initialize(values ?? {});
  }, [form, initialize, open]);

  const appearanceOptions = React.useMemo(
    () => appearances
      .filter((entry) => entry.id != null && entry.id.length > 0 && entry.isActive)
      .map((entry) => ({ value: entry.id!, label: entry.name || text("Unnamed format") })),
    [appearances, text]
  );

  return (
    <FormDrawer
      open={open}
      dirty={dirty}
      onCancel={onCancel}
      title="Settings"
      formId={FORM_ID}
      // „übernehmen": Der Drawer ändert nur den Arbeitsstand; hinaus geht das Schreiben erst mit
      // dem Versand.
      submitText="Apply"
    >
      <Form id={FORM_ID} form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <Form.Item
          name="documentTitle"
          label={text("Document title (digital file)")}
          tooltip={text("Send document title tooltip")}
        >
          <Input maxLength={200} placeholder={text("Subject")} />
        </Form.Item>
        {/*
          Leer heißt „wie gepflegt" — der Betreff der Vorlage samt seiner Variablen. Ihn hier
          vorzubelegen hieße, den aufgelösten Wortlaut einzufrieren und die Variablen zu verlieren.
        */}
        <Form.Item name="subject" label={text("Subject")} tooltip={text("Send document subject tooltip")}>
          <Input maxLength={200} placeholder={text("As maintained")} />
        </Form.Item>
        <Form.Item
          name="documentAppearanceId"
          label={text("Document appearance")}
          tooltip={text(canChooseAppearance
            ? "Send document appearance tooltip"
            : "Send document appearance permission missing")}
        >
          <Select disabled={!canChooseAppearance} options={appearanceOptions} />
        </Form.Item>
      </Form>
    </FormDrawer>
  );
}
